import { Box } from '@mui/material';
import type React from 'react';

import { borderRadius, spaces } from '../../../assets/styles/themes/tokens';
import { colors } from '../../../assets/styles/themes/variables';
import FormContainer from '../../../routes/onboarding/FormContainer';
import useScreenSize from '../../../utils/useScreenSize';
import OnboardingHeader from '../OnboardingHeader';
import OnboardingNavigationButtons from '../OnboardingNavigationButtons';

const TOTAL_STEPS = 6;

interface OnboardingStepProps {
	title: string;
	subtitle: string;
	children: React.ReactNode;
	stepNum: number;
	totalSteps?: number;
	onNext: () => void;
	onBack?: () => void;
	onNextIsDisabled?: boolean;
	compact?: boolean;
	contentHeights: {
		default: string;
		smallScreen: string;
	};
}

const OnboardingStepContainer: React.FC<OnboardingStepProps> = ({
	title,
	subtitle,
	children,
	stepNum,
	onNext,
	onBack,
	onNextIsDisabled,
	contentHeights,
	totalSteps = TOTAL_STEPS,
	compact = false,
}) => {
	const { isXsScreen } = useScreenSize();

	// Cross-browser thin scrollbars (webkit + Firefox) for consistent UX
	const scrollbarStyles = {
		'&::-webkit-scrollbar': {
			width: spaces.xs,
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: colors.neutral300,
			borderRadius: borderRadius.sm,
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: colors.neutral600,
		},
		scrollbarWidth: 'thin' as const,
		scrollbarColor: `${colors.neutral300} transparent`,
	};

	if (compact) {
		return (
			<Box
				data-testid={`onboarding-step-${stepNum}`}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '70vh',
				}}
			>
				<Box sx={{ paddingX: spaces.md, paddingTop: spaces.md, flexShrink: 0 }}>
					<OnboardingHeader title={title} subtitle={subtitle} hideIcon />
				</Box>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflowY: 'auto',
						paddingX: spaces.md,
						paddingY: spaces.sm,
						...scrollbarStyles,
					}}
				>
					{children}
				</Box>
				<Box sx={{ paddingX: spaces.md, paddingBottom: spaces.md, flexShrink: 0 }}>
					<OnboardingNavigationButtons
						stepNum={stepNum}
						totalSteps={totalSteps}
						onNext={onNext}
						onBack={onBack}
						onNextIsDisabled={onNextIsDisabled}
					/>
				</Box>
			</Box>
		);
	}

	const sharedBoxStyles = {
		display: 'flex',
		flexDirection: 'column',
		gap: spaces.xxxl,
	};

	const contentBoxStyles = {
		...sharedBoxStyles,
		paddingX: isXsScreen ? 0 : spaces.xxxxl,
		width: '100%',
	};

	return (
		<FormContainer>
			<Box data-testid={`onboarding-step-${stepNum}`} sx={sharedBoxStyles}>
				<Box data-testid={`onboarding-step-${stepNum}-content`} sx={contentBoxStyles}>
					<OnboardingHeader title={title} subtitle={subtitle} />
					<Box sx={{ width: '100%' }}>
						<Box
							sx={{
								overflowY: 'scroll',
								minHeight: !isXsScreen ? '264px' : undefined,
								height: isXsScreen ? contentHeights.smallScreen : contentHeights.default,
								...scrollbarStyles,
							}}
						>
							{children}
						</Box>
					</Box>
				</Box>
				<OnboardingNavigationButtons
					stepNum={stepNum}
					totalSteps={totalSteps}
					onNext={onNext}
					onBack={onBack}
					onNextIsDisabled={onNextIsDisabled}
				/>
			</Box>
		</FormContainer>
	);
};

export default OnboardingStepContainer;
